/*  MUI  */
import { Flex, Icon } from '@auviknetworks/iceberg-ui';
import { Button, IconButton, Typography } from '@mui/material';

/*  Shared  */
import { DataVisualCard } from 'application/Shared/Components/DataAPIVisualsCard';
import { FontSizes } from 'application/Shared/Constants';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

interface BreakdownData {
    id: string;
    label: string;
    value: number;
    onClick?: () => void;
}

/**
 * Breakdown card component that displays a list of data items
 * @component BreakdownCard
 * @param {Object} props - Component props
 * @param {BreakdownData[]} [props.data] - Array of breakdown data items to display
 * @param {boolean} [props.filteredResults] - Boolean indicating if results are filtered
 * @param {number} [props.maxTypes] - Maximum number of types to display
 * @param {string} [props.seeAllLabel] - Label for the "see all" button
 * @param {string} [props.title] - Title of the breakdown card
 * @param {string} [props.tooltip] - Tooltip text for the breakdown card
 * @returns {JSX.Element} Breakdown card component with data items
 */
export const BreakdownCard = ({
    data,
    filteredResults,
    maxTypes,
    seeAllLabel,
    onClickSeeAll,
    title,
    tooltip
}: {
    data: BreakdownData[];
    filteredResults?: boolean;
    maxTypes?: number;
    seeAllLabel?: string;
    onClickSeeAll?: () => unknown;
    title: string;
    tooltip?: string;
}): JSX.Element => {
    const { t } = useTranslation();
    const maxTypesToDisplay = maxTypes ? data.slice(0, maxTypes) : data;
    return (
        <DataVisualCard
            filteredResults={filteredResults}
            title={title}
            tooltip={tooltip}
        >
            <Flex alignItems="center" flexDirection="column" width="100%">
                <Flex
                    flexDirection="column"
                    width="100%"
                    alignItems="center"
                    justifyContent="center"
                    flex="1"
                >
                    {data.length === 0 && (
                        <Typography>{t('Common.noData')}</Typography>
                    )}
                    {maxTypesToDisplay.map((d) => (
                        <Flex
                            key={`option_${d.id}`}
                            alignItems="center"
                            justifyContent="space-between"
                            width="80%"
                        >
                            <Typography
                                data-testid={`breakdown-card-label-${d.id}`}
                            >
                                {d.label}
                            </Typography>

                            <Flex alignItems="center">
                                <Typography
                                    data-testid={`breakdown-card-value-${d.id}`}
                                >
                                    {d.value}
                                </Typography>
                                {d.onClick && (
                                    <IconButton
                                        data-trackid={`breakdownCard_${d.id}`}
                                        color="secondary"
                                        onClick={() => {
                                            // @ts-expect-error checking if d.onClick is not before rendering
                                            d.onClick();
                                        }}
                                    >
                                        <Icon
                                            size={FontSizes[1]}
                                            src="ChevronRight"
                                        />
                                    </IconButton>
                                )}
                            </Flex>
                        </Flex>
                    ))}
                </Flex>
                {maxTypes && data.length > maxTypes && (
                    <Flex alignSelf="flex-start">
                        <Button
                            data-trackid="breakdownCard_seeAll"
                            size="small"
                            sx={{
                                color: 'button.primary',
                                paddingLeft: '8px'
                            }}
                            onClick={onClickSeeAll}
                        >
                            {seeAllLabel || t('Common.seeAll')}
                        </Button>
                    </Flex>
                )}
            </Flex>
        </DataVisualCard>
    );
};
